import React, { useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";

import { PasswordInput, TextInput, Submit } from "../Forms";
import Spinner from "../Spinner";
import MessageBox from "../Global/MessageBox";

import "./Login.scss";
import { ApplicationState } from "../../reducers";
import { login } from "../../api/user";
import { UserActionType } from "../../reducers/user.reducer";
import { User } from "../../domain/domain";

const Header: React.FC = () => {
  return (
    <header className="app-header">
      <section className="container">
        <img className="app-logo app-logo--header" src="/images/xd-logo.png" alt="XDOME" />
      </section>
    </header>
  );
};

type LayoutProps = {
  children: React.ReactNode
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <>
      <Header />
      <main>{children}</main>
    </>
  );
};

type Props = {
  user: User,
  dispatch: (msg: any) => void
}

export const Login: React.FC<Props> = ({ dispatch }) => {
  const [username, updateUsername] = useState("");
  const [password, updatePassword] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    setError(null);

    if (username === "" || password === "") {
      setError("Please type a username and a password.");
      return;
    }

    setLoading(true);

    login(username, password).then(response => {
      localStorage.setItem('ACCESS_TOKEN', response.data.token);
      dispatch({ type: UserActionType.USER_LOADED, user: response.data.user });
    }).catch(error => {
      if (!error.response) {
        setError("Network error. Please check your connection and try again.");
        setLoading(false);
        return;
      }
      if (error.response.status === 0) {
        setError("Server is unreachable. Please contact a system administrator.");
      }
      else if (error.response.status === 401) {
        const errorData = error.response.data;  
        if (errorData && errorData.error === 'INVALID_CREDENTIALS') {
          setError(errorData.message || "Invalid username or password");
        } else {
          setError("Invalid username or password");
        }
        updatePassword("");
      }
      else if (error.response.status >= 400 && error.response.status <= 499) {
        const errorData = error.response.data;
        setError(errorData?.message || "An error occurred. Please try again.");
      }
      else if (error.response.status >= 500 && error.response.status <= 599) {
        setError("Server error. Please contact a system administrator.");
      }
    }).finally(() => {
      setLoading(false);
    })
  }

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateUsername(e.target.value);
    if (error) {
      setError(null);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updatePassword(e.target.value);
    if (error) {
      setError(null);
    }
  };

  return (
    <Layout>
      <section className="app-login">
        <div className="app-login-box">
          <h1 className="h2 m-xsmall-bottom-1">Log In</h1>
          {error && (
            <div className="m-xsmall-bottom-3">
              <MessageBox
                type="error"
                title="Oh No!"
                content={error}
              />
            </div>
          )}
          <form className="form" onSubmit={handleSubmit}>
            <TextInput
              name="username"
              label="Username"
              placeholder="Your username or email"
              value={username}
              onChange={handleUsernameChange}
            />
            <PasswordInput value={password} onChange={handlePasswordChange} />
            {/*<CheckboxInput name="rememberme" label="Remember Me?" />*/}
            <Submit>Login</Submit>
          </form>
          {loading && <Spinner />}
        </div>
      </section>
    </Layout>
  );

};

const mapStateToProps = (state: ApplicationState) => {
  return {
    user: state.user.currentUser
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return { dispatch };
}

export default compose<any>(
  connect(mapStateToProps, mapDispatchToProps)
)(Login);
